import { FC, ReactNode, useEffect } from 'react'

import { initLaunchDarkly, setFeatureFlagOverrides } from './launchDarklyClient'

/**
 * Starts the flag backend. Must wrap any component that calls
 * `useFeatureFlag`.
 *
 * The flag store itself is a module singleton read via `useSyncExternalStore`,
 * so this renders no context — it exists to own the client lifecycle and to keep
 * the provider shape the app already expects.
 */
export const FeatureFlagProvider: FC<{ children?: ReactNode }> = ({ children }) => {

  useEffect(() => {
    // On mount rather than at module load: module evaluation order relative to
    // the `/config.json` bootstrap is not guaranteed (Storybook imports this
    // module before seeding the runtime config), and a module-level side effect
    // would make that fragile ordering load-bearing.
    initLaunchDarkly()
  }, [])

  return <>{children}</>
}

/**
 * Serves fixed flag values with no network access — for Storybook and
 * storyshots. Per-story overrides come from `parameters.flags`, keyed by the
 * flag's wire name (the `FeatureFlag` enum *value*).
 */
export const OfflineFeatureFlagProvider: FC<{
  flags?: Record<string, boolean>,
  children?: ReactNode,
}> = ({ flags, children }) => {
  // Applied during render so children read the right values on first paint.
  // A plain idempotent assignment — it notifies nobody, because the re-render
  // that changes `flags` already re-reads every snapshot.
  //
  // Omitting `flags` means "offline, keep whatever values are already set", so
  // nesting this inside the decorator in `.storybook/preview.tsx` cannot wipe a
  // story's `parameters.flags`.
  setFeatureFlagOverrides(flags)

  return <>{children}</>
}
